import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminApi } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import {
  History, Save, RotateCcw, FlaskConical, Loader2, FileUp,
} from 'lucide-react';
import { StatusPill } from '../../components/StatusPills';
import { toast } from 'sonner';
import { useConfirm } from '../../components/ConfirmDialog';
import ProviderModelPicker from './ProviderModelPicker';

// Metadata per process — used for header copy. The nav order mirrors this list.
export const PROCESSES = {
  segmentation: {
    label: 'Segmentation',
    hint: 'Splits PDFs/chunks into individual records and classifies each.',
    hasPrompt: true,
  },
  record_matching: {
    label: 'Record matching',
    hint: 'Decides whether a new segment matches an existing record.',
    hasPrompt: true,
  },
  text_extraction: {
    label: 'Text extraction & tagging',
    hint: 'Pulls clean text and generates library + entity tags.',
    hasPrompt: true,
  },
  radar: {
    label: 'Copy comparison',
    hint: 'Reconstructs records from multiple sources, flags redactions and discrepancies.',
    hasPrompt: true,
  },
  email_classification: {
    label: 'Email classification',
    hint: 'Decides what category an incoming SAR email falls into (id_request, acknowledgment, …).',
    hasPrompt: false,
  },
  email_response_generation: {
    label: 'Email response generation',
    hint: 'Drafts auto-replies for routine SAR correspondence.',
    hasPrompt: false,
  },
  vision_ocr: {
    label: 'Vision OCR fallback',
    hint: 'Reads scanned PDF pages or images when local OCR returns insufficient text.',
    hasPrompt: false,
  },
  file_review: {
    label: 'Closer read of a file',
    hint: "When someone asks, reads one organisation's whole file for things worth a second look that the rules can't see. Every quote is checked against the records before it is shown.",
    hasPrompt: false,
  },
  org_summary: {
    label: 'Summary of time with an organisation',
    hint: "Once an organisation's documents are all analysed, reads the whole file and writes a careful summary of the person's time with them.",
    hasPrompt: false,
  },
  follow_up_scope_cleanup: {
    label: 'Follow-up wording cleanup',
    hint: 'Tidies what someone typed about missing records into clear request items for a follow-up letter, without adding facts.',
    hasPrompt: false,
  },
  feature_idea_match: {
    label: 'Feature idea matching',
    hint: 'Decides whether a new "Got an idea?" suggestion matches an existing one (adding a vote) or is genuinely new.',
    hasPrompt: false,
  },
};

const REPROCESS = [
  { id: 'default', label: 'Default — reprocess if confidence below threshold' },
  { id: 'force', label: 'Force — reprocess every existing document' },
  { id: 'silent', label: 'Silent — apply to new documents only' },
];

const AdminProcessDetail = () => {
  const { processId } = useParams();
  const navigate = useNavigate();
  const meta = PROCESSES[processId];

  const [activePrompt, setActivePrompt] = useState(null);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loadingPrompt, setLoadingPrompt] = useState(meta?.hasPrompt || false);

  const [runs, setRuns] = useState({ results: [], note: '' });
  const [loadingRuns, setLoadingRuns] = useState(true);

  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyVersions, setHistoryVersions] = useState([]);

  // Test prompt state
  const [testOpen, setTestOpen] = useState(false);
  const [testRunning, setTestRunning] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testFile, setTestFile] = useState(null);
  const [testRawText, setTestRawText] = useState('');
  const [testSecondaryText, setTestSecondaryText] = useState('');
  const [testPrimaryOrg, setTestPrimaryOrg] = useState('');
  const [testSecondaryOrg, setTestSecondaryOrg] = useState('');

  useEffect(() => {
    if (!meta) return;
    if (!meta.hasPrompt) {
      setActivePrompt(null);
      setEdit(null);
      setLoadingPrompt(false);
      return;
    }
    let cancelled = false;
    setLoadingPrompt(true);
    adminApi
      .getPrompt(processId)
      .then((res) => {
        if (cancelled) return;
        const p = res.data || {};
        setActivePrompt(p);
        setEdit({
          prompt: p.prompt || '',
          change_note: '',
          reprocess_behaviour: 'default',
          confidence_threshold: p.confidence_threshold ?? 0.75,
          chunk_pages: p.chunk_pages ?? 8,
          chunk_overlap_pages: p.chunk_overlap_pages ?? 2,
        });
      })
      .catch((e) => {
        if (!cancelled) toast.error(e?.response?.data?.detail || 'Failed to load prompt');
      })
      .finally(() => !cancelled && setLoadingPrompt(false));
    return () => { cancelled = true; };
  }, [processId, meta]);

  useEffect(() => {
    let cancelled = false;
    setLoadingRuns(true);
    adminApi
      .flaggedRuns(processId)
      .then((res) => !cancelled && setRuns(res.data || { results: [] }))
      .finally(() => !cancelled && setLoadingRuns(false));
    return () => { cancelled = true; };
  }, [processId]);

  const dirty = useMemo(() => {
    if (!meta?.hasPrompt || !edit || !activePrompt) return false;
    const chunkDirty = processId === 'segmentation' && (
      Number(edit.chunk_pages) !== Number(activePrompt.chunk_pages ?? 8) ||
      Number(edit.chunk_overlap_pages) !== Number(activePrompt.chunk_overlap_pages ?? 2)
    );
    return (
      (edit.prompt || '') !== (activePrompt.prompt || '') ||
      Number(edit.confidence_threshold) !== Number(activePrompt.confidence_threshold ?? 0.75) ||
      chunkDirty
    );
  }, [edit, activePrompt, processId, meta]);

  const onChange = (field, value) => setEdit((p) => ({ ...p, [field]: value }));

  const savePrompt = async () => {
    if (!edit?.change_note?.trim()) {
      toast.error('Change note is required to save a new version.');
      return;
    }
    setSaving(true);
    try {
      const body = {
        process_id: processId,
        prompt: edit.prompt,
        change_note: edit.change_note.trim(),
        reprocess_behaviour: edit.reprocess_behaviour,
        confidence_threshold: Number(edit.confidence_threshold),
      };
      if (processId === 'segmentation') {
        body.chunk_pages = Math.max(1, parseInt(edit.chunk_pages, 10) || 8);
        body.chunk_overlap_pages = Math.max(0, parseInt(edit.chunk_overlap_pages, 10) || 0);
        if (body.chunk_overlap_pages >= body.chunk_pages) {
          body.chunk_overlap_pages = body.chunk_pages - 1;
        }
      }
      await adminApi.savePrompt(processId, body);
      toast.success('Saved new version');
      const res = await adminApi.getPrompt(processId);
      const p = res.data || {};
      setActivePrompt(p);
      setEdit({
        prompt: p.prompt || '',
        change_note: '',
        reprocess_behaviour: 'default',
        confidence_threshold: p.confidence_threshold ?? 0.75,
        chunk_pages: p.chunk_pages ?? 8,
        chunk_overlap_pages: p.chunk_overlap_pages ?? 2,
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save prompt');
    } finally {
      setSaving(false);
    }
  };

  const openTest = () => {
    setTestResult(null);
    setTestFile(null);
    setTestRawText('');
    setTestSecondaryText('');
    setTestPrimaryOrg('');
    setTestSecondaryOrg('');
    setTestOpen(true);
  };

  const runTest = async () => {
    if (!edit?.prompt?.trim()) {
      toast.error('Write a prompt first.');
      return;
    }
    const fd = new FormData();
    fd.append('prompt', edit.prompt);
    if (testFile) fd.append('file', testFile);
    if (testRawText.trim()) fd.append('raw_text', testRawText);
    if (testSecondaryText.trim()) fd.append('secondary_text', testSecondaryText);
    if (testPrimaryOrg.trim()) fd.append('primary_org', testPrimaryOrg);
    if (testSecondaryOrg.trim()) fd.append('secondary_org', testSecondaryOrg);
    if (processId === 'segmentation' && edit.chunk_pages) {
      fd.append('chunk_pages_override', String(edit.chunk_pages));
    }
    setTestRunning(true);
    setTestResult(null);
    try {
      const res = await adminApi.testPrompt(processId, fd);
      setTestResult(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test failed');
    } finally {
      setTestRunning(false);
    }
  };

  const openHistory = async () => {
    setHistoryOpen(true);
    setHistoryVersions([]);
    try {
      const res = await adminApi.promptHistory(processId);
      setHistoryVersions(res.data?.versions || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load history');
    }
  };

  const confirmDialog = useConfirm();
  const restore = async (version) => {
    if (!(await confirmDialog({ title: `Restore version ${version}?`, description: 'It becomes a new active version; the current one stays in the history.', confirmLabel: 'Restore' }))) return;
    try {
      await adminApi.restorePrompt(processId, version);
      toast.success(`Version ${version} restored`);
      setHistoryOpen(false);
      const res = await adminApi.getPrompt(processId);
      const p = res.data || {};
      setActivePrompt(p);
      setEdit((prev) => ({ ...prev, prompt: p.prompt || '', change_note: '' }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to restore');
    }
  };

  if (!meta) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">Unknown process.</p>
        <Button variant="secondary" onClick={() => navigate('/admin/analysis')}>Back to Analysis</Button>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid={`admin-process-${processId}`}>
      <div>
        <h1 className="font-heading text-2xl font-semibold text-foreground">{meta.label}</h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">{meta.hint}</p>
      </div>

      {/* ---------- PROMPT ---------- */}
      {meta.hasPrompt && (
        <section className="space-y-3" data-testid="process-prompt-section">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <h2 className="font-heading text-lg font-semibold text-foreground">Prompt</h2>
              {activePrompt?.version != null && (
                <Badge variant="secondary" data-testid="process-active-version">
                  Active v{activePrompt.version}
                </Badge>
              )}
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={openHistory}
              data-testid="process-history-btn"
            >
              <History className="w-3.5 h-3.5 mr-1.5" />
              History
            </Button>
          </div>

          {loadingPrompt ? (
            <p className="text-sm text-muted-foreground">Loading prompt…</p>
          ) : edit ? (
            <Card>
              <CardContent className="p-5 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="prompt-text">Prompt template</Label>
                  <Textarea
                    id="prompt-text"
                    value={edit.prompt}
                    onChange={(ev) => onChange('prompt', ev.target.value)}
                    rows={14}
                    className="font-mono text-xs leading-relaxed"
                    data-testid="process-prompt-textarea"
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="threshold">Confidence threshold (0-1)</Label>
                    <Input
                      id="threshold"
                      type="number"
                      min="0" max="1" step="0.01"
                      value={edit.confidence_threshold}
                      onChange={(ev) => onChange('confidence_threshold', ev.target.value)}
                      data-testid="process-threshold"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Reprocess behaviour</Label>
                    <Select
                      value={edit.reprocess_behaviour}
                      onValueChange={(v) => onChange('reprocess_behaviour', v)}
                    >
                      <SelectTrigger data-testid="process-reprocess">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {REPROCESS.map((r) => (
                          <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {processId === 'segmentation' && (
                  <div className="grid sm:grid-cols-2 gap-4 rounded-lg border border-rule p-4">
                    <div className="sm:col-span-2">
                      <p className="text-xs font-medium text-foreground">Chunking strategy</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        How long PDFs are sliced before segmentation. Overlap lets the LLM
                        see context at chunk boundaries so records spanning two chunks are not split.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="chunk-pages">Pages per chunk</Label>
                      <Input
                        id="chunk-pages"
                        type="number" min="1" max="40" step="1"
                        value={edit.chunk_pages}
                        onChange={(ev) => onChange('chunk_pages', ev.target.value)}
                        data-testid="process-chunk-pages"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="chunk-overlap">Overlap pages</Label>
                      <Input
                        id="chunk-overlap"
                        type="number" min="0" max="10" step="1"
                        value={edit.chunk_overlap_pages}
                        onChange={(ev) => onChange('chunk_overlap_pages', ev.target.value)}
                        data-testid="process-chunk-overlap"
                      />
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="change-note">Change note (required)</Label>
                  <Input
                    id="change-note"
                    value={edit.change_note}
                    onChange={(ev) => onChange('change_note', ev.target.value)}
                    placeholder="What changed and why?"
                    data-testid="process-change-note"
                  />
                </div>

                <div className="flex items-center justify-end gap-2 pt-1 flex-wrap">
                  <Button
                    variant="secondary"
                    onClick={openTest}
                    disabled={!edit?.prompt?.trim()}
                    data-testid="process-test-prompt"
                  >
                    <FlaskConical className="w-4 h-4 mr-1.5" />
                    Test this prompt
                  </Button>
                  <Button
                    disabled={!dirty || saving || !edit.change_note?.trim()}
                    onClick={savePrompt}
                    data-testid="process-save-prompt"
                  >
                    <Save className="w-4 h-4 mr-1.5" />
                    Save new version
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <p className="text-sm text-muted-foreground">No prompt configured.</p>
          )}
        </section>
      )}

      {/* ---------- MODEL ---------- */}
      <section className="space-y-3" data-testid="process-model-section">
        <h2 className="font-heading text-lg font-semibold text-foreground">Model</h2>
        <Card>
          <CardContent className="p-5">
            <ProviderModelPicker processId={processId} />
          </CardContent>
        </Card>
      </section>

      {/* ---------- ACTION LOG ---------- */}
      <section className="space-y-3" data-testid="process-action-log">
        <div className="flex items-center gap-2 flex-wrap">
          <h2 className="font-heading text-lg font-semibold text-foreground">Action log</h2>
          <span className="text-xs text-muted-foreground">
            Low-confidence, errored or fallback runs for this process
          </span>
        </div>
        {loadingRuns ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : runs.results?.length ? (
          <div className="space-y-3">
            {runs.results.map((r) => {
              const isFallback = r.event_type === 'fallback_to_system_provider';
              return (
                <Card key={r.id} className="bg-ground">
                  <CardContent className="p-4 space-y-2">
                    <div className="flex items-start gap-2">
                      <StatusPill tone="needs" size="sm" className="mt-0.5">Flagged</StatusPill>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-foreground text-sm">
                          {r.flag_reason || 'Flagged'}
                        </p>
                        {r.document_id && (
                          <p className="text-xs text-muted-foreground mt-0.5">
                            doc {r.document_id}
                          </p>
                        )}
                      </div>
                      {r.created_at && (
                        <span className="text-[11px] text-muted-foreground whitespace-nowrap">
                          {new Date(r.created_at).toLocaleString()}
                        </span>
                      )}
                    </div>
                    {(r.ai_provider_name || r.ai_model_used || isFallback) && (
                      <div className="flex items-center gap-2 flex-wrap">
                        {r.ai_provider_name && (
                          <Badge variant="secondary" className="text-[10px]">
                            {r.ai_provider_name} ({r.ai_provider_type})
                          </Badge>
                        )}
                        {r.ai_model_used && (
                          <Badge variant="secondary" className="text-[10px] font-mono">
                            {r.ai_model_used}
                          </Badge>
                        )}
                        {r.ai_model_requested && r.ai_model_used !== r.ai_model_requested && (
                          <span className="text-[10px] text-muted-foreground">
                            (requested {r.ai_model_requested})
                          </span>
                        )}
                        {isFallback && r.primary_provider_name && (
                          <StatusPill tone="breach" size="sm">
                            Fell back from {r.primary_provider_name} / {r.primary_model}
                          </StatusPill>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card>
            <CardContent className="p-6 text-center text-muted-foreground space-y-2">
              <p className="text-sm">{runs.note || 'Nothing flagged for this process.'}</p>
            </CardContent>
          </Card>
        )}
      </section>

      {/* Test prompt dialog */}
      <Dialog open={testOpen} onOpenChange={setTestOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="process-test-dialog">
          <DialogHeader>
            <DialogTitle>Test prompt: {meta.label}</DialogTitle>
            <DialogDescription>
              Your draft prompt runs through the currently configured model. Nothing is
              saved, no records are created, no pipeline state changes.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            {/* Input area depends on process */}
            {(processId === 'segmentation' || processId === 'text_extraction') && (
              <div className="space-y-2">
                <Label htmlFor="test-file">Upload a PDF or image</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="test-file"
                    type="file"
                    accept="application/pdf,image/*"
                    onChange={(ev) => setTestFile(ev.target.files?.[0] || null)}
                    data-testid="process-test-file"
                  />
                  {testFile && (
                    <Badge variant="secondary" className="whitespace-nowrap">
                      <FileUp className="w-3 h-3 mr-1" />
                      {testFile.name}
                    </Badge>
                  )}
                </div>
                {processId === 'text_extraction' && (
                  <>
                    <p className="text-xs text-muted-foreground">Or paste raw text</p>
                    <Textarea
                      rows={4}
                      value={testRawText}
                      onChange={(e) => setTestRawText(e.target.value)}
                      placeholder="Paste text to extract from (instead of uploading a file)…"
                      data-testid="process-test-raw-text"
                    />
                  </>
                )}
              </div>
            )}

            {processId === 'record_matching' && (
              <div className="space-y-2">
                <Label>Segment text</Label>
                <Textarea
                  rows={6}
                  value={testRawText}
                  onChange={(e) => setTestRawText(e.target.value)}
                  placeholder="Paste the segment text you want to match…"
                  data-testid="process-test-raw-text"
                />
                <p className="text-xs text-muted-foreground">
                  The test runs without existing records, so the model should decide "new".
                </p>
              </div>
            )}

            {processId === 'radar' && (
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label>Primary source (originating org)</Label>
                  <Input
                    placeholder="Primary organisation name"
                    value={testPrimaryOrg}
                    onChange={(e) => setTestPrimaryOrg(e.target.value)}
                  />
                  <Textarea
                    rows={5}
                    value={testRawText}
                    onChange={(e) => setTestRawText(e.target.value)}
                    placeholder="Paste the primary source text, including [REDACTED:*] markers…"
                    data-testid="process-test-raw-text"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Secondary source</Label>
                  <Input
                    placeholder="Secondary organisation name"
                    value={testSecondaryOrg}
                    onChange={(e) => setTestSecondaryOrg(e.target.value)}
                  />
                  <Textarea
                    rows={5}
                    value={testSecondaryText}
                    onChange={(e) => setTestSecondaryText(e.target.value)}
                    placeholder="Paste the secondary source text…"
                    data-testid="process-test-secondary-text"
                  />
                </div>
              </div>
            )}

            <Button
              onClick={runTest}
              disabled={testRunning}
              className="w-full"
              data-testid="process-test-run"
            >
              {testRunning ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Running…</>
              ) : (
                <><FlaskConical className="w-4 h-4 mr-2" /> Run test</>
              )}
            </Button>

            {/* Result */}
            {testResult && (
              <div className="space-y-3" data-testid="process-test-result">
                <div className="flex items-center gap-2 flex-wrap text-xs">
                  <Badge variant="secondary">{testResult.duration_ms}ms</Badge>
                  {testResult.ai?.provider_name && (
                    <Badge variant="secondary">
                      {testResult.ai.provider_name} ({testResult.ai.provider_type})
                    </Badge>
                  )}
                  {testResult.ai?.model_used && (
                    <Badge variant="secondary" className="font-mono">
                      {testResult.ai.model_used}
                    </Badge>
                  )}
                  {testResult.meta?.total_pages != null && (
                    <Badge variant="secondary">
                      {testResult.meta.total_pages} pages read
                    </Badge>
                  )}
                  {testResult.meta?.chunks_would_create && (
                    <Badge variant="secondary">
                      {testResult.meta.chunks_would_create} chunks (testing first)
                    </Badge>
                  )}
                </div>

                {testResult.parse_error && (
                  <div className="rounded-lg bg-breach-wash p-3 text-xs text-breach-ink">
                    <p className="font-medium">Could not parse JSON from the response</p>
                    <p className="mt-1">{testResult.parse_error}</p>
                  </div>
                )}

                {testResult.parsed_output && (
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-foreground">Parsed output</p>
                    <pre className="bg-ground rounded-md p-3 text-xs font-mono whitespace-pre-wrap max-h-80 overflow-y-auto">
                      {JSON.stringify(testResult.parsed_output, null, 2)}
                    </pre>
                  </div>
                )}

                <details className="text-xs">
                  <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                    Raw model output
                  </summary>
                  <pre className="bg-ground rounded-md p-3 font-mono whitespace-pre-wrap max-h-80 overflow-y-auto mt-2">
                    {testResult.raw_output}
                  </pre>
                </details>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="secondary" onClick={() => setTestOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="process-history-dialog">
          <DialogHeader>
            <DialogTitle>Version history: {meta.label}</DialogTitle>
            <DialogDescription>
              Each save appends a new immutable version. Restoring re-applies an older
              prompt as a fresh version.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            {historyVersions.length === 0 ? (
              <p className="text-sm text-muted-foreground">No history yet.</p>
            ) : historyVersions.map((v) => (
              <Card key={v.id} data-testid={`process-history-row-${v.version}`}>
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="secondary">v{v.version}</Badge>
                      <span className="text-xs text-muted-foreground">
                        {new Date(v.created_at).toLocaleString()}
                      </span>
                      {v.confidence_threshold != null && (
                        <Badge variant="secondary" className="text-xs">
                          threshold {v.confidence_threshold}
                        </Badge>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => restore(v.version)}
                      data-testid={`process-history-restore-${v.version}`}
                    >
                      <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                      Restore
                    </Button>
                  </div>
                  {v.change_note && (
                    <p className="text-sm text-foreground italic">"{v.change_note}"</p>
                  )}
                  <pre className="bg-ground rounded-md p-3 text-xs font-mono whitespace-pre-wrap max-h-48 overflow-y-auto">
                    {v.prompt}
                  </pre>
                </CardContent>
              </Card>
            ))}
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setHistoryOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminProcessDetail;
